import type { ReactNode } from 'react'
import { useColumnWidths } from '../hooks/useColumnWidths'
import type { SortDir } from '../hooks/useServerList'

// Column-config driven table with click-to-sort headers and per-user resizable
// columns (widths persisted via useColumnWidths). Replaces hand-rolled <table>s
// so every list can adopt the same behaviour. See [[table-list-pattern]].

export interface Column<T> {
    key: string
    header: ReactNode
    sortField?: string        // set → header is clickable for server-side sort
    className?: string        // applied to th + td (e.g. 'num')
    defaultWidth?: number     // px, before the user resizes
    sticky?: boolean          // fixed width, docked to the right edge (sticks while scrolling)
    render: (row: T) => ReactNode
}

interface Props<T> {
    tableId: string
    columns: Column<T>[]
    rows: T[]
    rowKey: (row: T) => string
    sort?: string
    dir?: SortDir
    onSort?: (field: string) => void
    hidden?: Set<string>      // column keys the user has hidden
    onRowActivate?: (row: T) => void // double-click a row → e.g. open the editor
}

export default function DataTable<T>({ tableId, columns, rows, rowKey, sort, dir, onSort, hidden, onRowActivate }: Props<T>) {
    // Widths are tracked for ALL columns (so a hidden column keeps its width when
    // shown again); only the visible ones are rendered.
    const { widths, startResize } = useColumnWidths(tableId, columns)
    const visible = hidden ? columns.filter((c) => !hidden.has(c.key)) : columns

    // Sticky columns keep a fixed width and dock right; a flexible spacer column
    // between the flowing columns and the sticky ones soaks up any leftover width
    // (so flow columns keep their set width, sticky columns stay fixed, and the
    // table still fills 100%). When columns overflow the spacer collapses to 0 and
    // the sticky column stays pinned to the right while the rest scrolls.
    const stickyCols = visible.filter((c) => c.sticky)
    const flowCols = visible.filter((c) => !c.sticky)
    const hasSticky = stickyCols.length > 0

    const headerCell = (c: Column<T>, showResizer: boolean) => {
        const sortable = !!(c.sortField && onSort)
        const active = sortable && sort === c.sortField
        return (
            <th
                key={c.key}
                className={[c.className, sortable ? 'sortable' : '', active ? 'active' : '', c.sticky ? 'sticky-right' : ''].filter(Boolean).join(' ') || undefined}
                onClick={sortable ? () => onSort!(c.sortField!) : undefined}
                aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
            >
                <span className="th-label">
                    {c.header}
                    {sortable && <span className="sort-arrow">{active ? (dir === 'asc' ? '▲' : '▼') : ''}</span>}
                </span>
                {showResizer && (
                    <span className="col-resizer" onMouseDown={(e) => startResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
                )}
            </th>
        )
    }

    const bodyCell = (c: Column<T>, row: T) => (
        <td key={c.key} className={[c.className, c.sticky ? 'sticky-right' : ''].filter(Boolean).join(' ') || undefined}>
            {c.render(row)}
        </td>
    )

    return (
        <div className="table-scroll">
        <table className="fixed">
            <colgroup>
                {flowCols.map((c) => <col key={c.key} style={{ width: widths[c.key] }} />)}
                {hasSticky && <col key="__spacer" />}
                {stickyCols.map((c) => <col key={c.key} style={{ width: widths[c.key] }} />)}
            </colgroup>
            <thead>
                <tr>
                    {flowCols.map((c, i) => headerCell(c, i < flowCols.length - 1 || hasSticky))}
                    {hasSticky && <th key="__spacer" className="col-spacer" aria-hidden="true" />}
                    {stickyCols.map((c) => headerCell(c, false))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr
                        key={rowKey(row)}
                        className={onRowActivate ? 'row-activatable' : undefined}
                        onDoubleClick={onRowActivate ? () => onRowActivate(row) : undefined}
                        // suppress the text selection a double-click would otherwise make
                        onMouseDown={onRowActivate ? (e) => { if (e.detail > 1) e.preventDefault() } : undefined}
                    >
                        {flowCols.map((c) => bodyCell(c, row))}
                        {hasSticky && <td key="__spacer" className="col-spacer" />}
                        {stickyCols.map((c) => bodyCell(c, row))}
                    </tr>
                ))}
            </tbody>
        </table>
        </div>
    )
}
